import type { Property } from "csstype";
import { type MapMouseEvent } from "mapbox-gl";
import type { MapGeoJSONFeature } from "maplibre-gl";
import { useState } from "react";
import { type MapMode } from "src/features/collection-map/components/types";
import { match } from "ts-pattern";
import { type MaplibreRef, useMaplibre } from "../utils";

interface useMapCursorStylingProps {
  mapId: string;
  mode: MapMode;
}

const editCustomPolygonLayerPointCursorIds = [
  "edit-location-polygon-fill-inactive.hot",
  "edit-location-polygon-fill-inactive.cold",
  "edit-location-polygon-midpoint.cold",
  "edit-location-polygon-and-line-vertex-stroke-inactive.cold",
  "edit-location-polygon-and-line-vertex-stroke-inactive.hot",
  "edit-location-polygon-and-line-vertex-inactive.cold",
  "edit-location-polygon-and-line-vertex-inactive.hot",
];
// TODO: [HORT-2803] the move cursor features wont appear until a feature has been interacted with, subsequently querying this too early MouseMove is causing console errors to build up. Solution would be the ability to do a partial match on the layer id
const editCustomPolygonLayerMoveCursorIds = [
  "edit-location-polygon-fill-active.hot",
  "gl-draw-point-stroke-active.hot",
  "gl-draw-point-stroke-active.cold",
];

const customPolygonLayerIds = [
  "location-polygon-fill",
  "location-polygon-line",
];
const clusterLayerIds = ["clusters", "cluster-count", "unclustered-point"];

export const getTopLevelFeature = ({
  event,
  mode,
  ourMap,
}: {
  event: MapMouseEvent;
  mode: MapMode;
  ourMap?: MaplibreRef | undefined;
}) => {
  const layers = match(mode)
    .with("view", () => [...clusterLayerIds, ...customPolygonLayerIds])
    .with("editLocation", () => [
      ...editCustomPolygonLayerPointCursorIds,
      ...editCustomPolygonLayerMoveCursorIds,
    ])
    .otherwise(() => undefined);

  const filteredLayers = layers?.filter((l) => ourMap?.getLayer(l) != null);

  const customFeatures = ourMap?.queryRenderedFeatures(
    [event.point.x, event.point.y],
    {
      layers: filteredLayers,
    },
  );

  return { topLevelFeature: customFeatures?.[0] };
};

export const useMapCursorStyling = ({
  mapId,
  mode,
}: useMapCursorStylingProps) => {
  const { [mapId]: ourMap } = useMaplibre();
  const [cursor, setCursor] = useState<Property.Cursor>("auto");
  const [topLevelFeature, setTopLevelFeature] = useState<
    MapGeoJSONFeature | undefined
  >();

  const handleMouseMove = (event: MapMouseEvent) => {
    const feature = getTopLevelFeature({
      event,
      mode,
      ourMap,
    });

    setTopLevelFeature(feature.topLevelFeature);

    if (topLevelFeature == null) {
      setCursor("auto");
    } else if (
      clusterLayerIds.includes(topLevelFeature.layer.id) ||
      customPolygonLayerIds.includes(topLevelFeature.layer.id)
    ) {
      setCursor("pointer");
    } else if (mode === "editLocation") {
      if (ourMap) {
        ourMap.getCanvas().style.cursor = "inherit";
      }
    } else {
      setCursor("auto");
    }
  };

  return { cursor, handleMouseMove, topLevelFeature };
};
